/**
 * F1-F12 user-mapped shortcuts. Maps are persisted in localStorage.
 * Press F-key to execute the bound command; Ctrl+F-key to open the
 * mapping modal for that slot.
 *
 * Defaults mirror Bloomberg-style keystroke commands: F1=help,
 * F2=portfolio, F3=markets, etc. Simon can rebind any slot.
 */
import { useCallback, useEffect, useState } from 'react'
import { clsx } from 'clsx'
import { Keyboard, X } from 'lucide-react'

const LOCALSTORAGE_KEY = 'terminal_fkey_shortcuts_v1'

const DEFAULTS: Record<string, string> = {
  F1:  '/help',
  F2:  '/portfolio',
  F3:  '/blotter',
  F4:  '/regime',
  F5:  '/indices',
  F6:  '/signals',
  F7:  '/whales',
  F8:  '/alerts',
  F9:  '/news',
  F10: '/summary',
  F11: '/lookthrough',
  F12: '/tearsheet',
}

function loadShortcuts(): Record<string, string> {
  try {
    const raw = localStorage.getItem(LOCALSTORAGE_KEY)
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) }
  } catch {}
  return { ...DEFAULTS }
}

function saveShortcuts(map: Record<string, string>) {
  try {
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(map))
  } catch {}
}

interface Props {
  onExecute: (command: string) => void
}

export function FKeyShortcuts({ onExecute }: Props) {
  const [map, setMap] = useState<Record<string, string>>(() => loadShortcuts())
  const [editing, setEditing] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [showLegend, setShowLegend] = useState(false)

  // F-key listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const m = e.key.match(/^F(\d{1,2})$/)
      if (!m) return
      const slot = `F${m[1]}`
      if (!(slot in DEFAULTS)) return

      // Ignore F-keys when typing in text inputs/textareas — those slots
      // may be interpreted by the browser (F5 reload, F11 fullscreen)
      // but if pressed while focused in our terminal input, take them.
      const target = e.target as HTMLElement
      const inInput = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA'

      if (e.ctrlKey || e.metaKey) {
        // Ctrl+F-key → open rebind modal
        e.preventDefault()
        setEditing(slot)
        setEditValue(map[slot] || '')
        return
      }

      // Plain F-key → execute the mapping
      const cmd = map[slot]
      if (cmd && inInput) {
        e.preventDefault()
        onExecute(cmd)
      } else if (cmd && !inInput) {
        // Not focused in an input — still intercept (user probably wants shortcut)
        // but skip if the slot would cause browser chrome action (F5, F11, F12)
        if (slot === 'F5' || slot === 'F11' || slot === 'F12') return
        e.preventDefault()
        onExecute(cmd)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [map, onExecute])

  const saveEdit = useCallback(() => {
    if (!editing) return
    const trimmed = editValue.trim()
    const next = { ...map }
    if (trimmed) next[editing] = trimmed.startsWith('/') ? trimmed : '/' + trimmed
    else next[editing] = DEFAULTS[editing]  // reset to default on empty
    setMap(next)
    saveShortcuts(next)
    setEditing(null)
  }, [editing, editValue, map])

  const resetAll = useCallback(() => {
    setMap({ ...DEFAULTS })
    saveShortcuts({ ...DEFAULTS })
  }, [])

  return (
    <>
      {/* Legend toggle button (bottom-right corner) */}
      <button
        type="button"
        onClick={() => setShowLegend(s => !s)}
        className="fixed bottom-4 right-4 z-40 text-[10px] font-mono text-crt-dim hover:text-crt-green px-2 py-1 border border-crt-dim/30 rounded bg-black/70 flex items-center gap-1"
        title="F1-F12 shortcuts (Ctrl+F-key to rebind)"
      >
        <Keyboard size={11} />
        F-keys
      </button>

      {/* Legend panel */}
      {showLegend && (
        <div className="fixed bottom-14 right-4 z-40 bg-surface-card border border-crt-green/40 rounded-lg shadow-2xl p-3 w-80 text-xs font-mono">
          <div className="flex items-center justify-between mb-2">
            <span className="text-crt-bright font-bold">F-key bindings</span>
            <div className="flex gap-2">
              <button type="button" onClick={resetAll}
                      className="text-[10px] text-crt-dim hover:text-crt-amber">reset</button>
              <button type="button" onClick={() => setShowLegend(false)}
                      className="text-crt-dim hover:text-crt-green"><X size={12} /></button>
            </div>
          </div>
          <div className="space-y-0.5">
            {Object.keys(DEFAULTS).map(slot => (
              <button
                key={slot}
                type="button"
                onClick={() => { setEditing(slot); setEditValue(map[slot] || '') }}
                className="w-full flex items-center gap-2 px-1.5 py-1 rounded hover:bg-crt-green/10 text-left"
                title="Click to rebind"
              >
                <kbd className="text-crt-amber shrink-0 w-8">{slot}</kbd>
                <span className="text-crt-green truncate">{map[slot] || '—'}</span>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-crt-dim mt-2 pt-2 border-t border-crt-dim/30">
            Press F-key to run · Ctrl+F-key to rebind · F5/F11/F12 reserved for browser
          </p>
        </div>
      )}

      {/* Rebind modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setEditing(null)}
        >
          <div
            className="w-full max-w-md mx-4 bg-surface-card border border-crt-green/40 rounded-lg p-4 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-crt-bright font-mono font-bold">
                Rebind <span className="text-crt-amber">{editing}</span>
              </span>
              <button type="button" onClick={() => setEditing(null)} className="text-crt-dim hover:text-crt-green">
                <X size={14} />
              </button>
            </div>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-crt-dim">Command</span>
              <input
                type="text"
                autoFocus
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null) }}
                placeholder="/set NVDA"
                aria-label={`Command for ${editing}`}
                className={clsx(
                  'w-full mt-1 px-3 py-2 bg-black/40 border border-crt-green/30',
                  'text-crt-green font-mono text-sm rounded outline-none focus:border-crt-green/60',
                )}
              />
            </label>
            <div className="flex items-center justify-between mt-3">
              <span className="text-[10px] text-crt-dim font-mono">
                Default: <span className="text-crt-green">{DEFAULTS[editing]}</span>
              </span>
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditing(null)}
                        className="text-xs text-crt-dim hover:text-crt-green px-3 py-1">
                  Cancel
                </button>
                <button type="button" onClick={saveEdit}
                        className="text-xs text-crt-bright bg-crt-green/20 hover:bg-crt-green/30 px-3 py-1 rounded border border-crt-green/40">
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
